import React, { useState, useEffect } from 'react';
import { RecorderStatusResponse, ActiveRecordingSession } from '../../types';
import { statusService } from '../../services/api';
import { formatDateTime } from '../../utils/datetime';

const POLL_INTERVAL_MS = 10000;
// How long "starting…" stands before the request is called lost. launchd wakes
// the start agent on the write, so a recorder that came up reports its first
// heartbeat within a poll or two; past this, the agent never ran.
const START_TIMEOUT_MS = 45000;

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, exp)).toFixed(exp === 0 ? 0 : 1)} ${units[exp]}`;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

const StatusPage: React.FC = () => {
  const [statusResp, setStatusResp] = useState<RecorderStatusResponse | null>(null);
  const [activeRecordings, setActiveRecordings] = useState<ActiveRecordingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [controlError, setControlError] = useState<string | null>(null);
  // When a start was asked for. A cold start has no equivalent of
  // restart_requested to read back — the flag lives in a file the API cannot
  // see the agent consume — so the pending state is local, and the recorder
  // reporting in is what ends it.
  const [startRequestedAt, setStartRequestedAt] = useState<number | null>(null);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const [status, active] = await Promise.all([
        statusService.getRecorderStatus(),
        statusService.getActiveRecordings(),
      ]);
      setStatusResp(status);
      setActiveRecordings(active);
      setError(null);
      setLastFetched(new Date());
    } catch (err) {
      setError('Failed to fetch recorder status');
      console.error('Error fetching recorder status:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const dataInterval = setInterval(fetchData, POLL_INTERVAL_MS);
    const tickInterval = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(dataInterval);
      clearInterval(tickInterval);
    };
  }, []);

  // A recorder that has reported in is the end of any pending start, however
  // it got there — this button, a terminal, or a colleague on another console.
  useEffect(() => {
    if (statusResp?.is_alive) setStartRequestedAt(null);
  }, [statusResp]);

  // Asks the API to flag a restart; the recorder picks the flag up on its
  // next ~10s heartbeat. The refetch makes the "restart pending" state
  // (info.restart_requested) show up immediately rather than on the next poll.
  const handleRestart = async () => {
    setConfirmRestart(false);
    try {
      await statusService.requestRestart();
      setControlError(null);
      await fetchData();
    } catch (err: any) {
      setControlError(err?.response?.data?.error || 'Failed to request restart');
      console.error('Error requesting recorder restart:', err);
    }
  };

  // The cold-start path: the API writes a request file that a launchd agent
  // watches, and the agent starts the recorder as the user who owns the
  // recordings. Unlike a restart there is nothing running to confirm receipt,
  // so the button's own timer (START_TIMEOUT_MS) is what reports a failure.
  const handleStart = async () => {
    try {
      await statusService.requestStart();
      setControlError(null);
      setStartRequestedAt(Date.now());
      await fetchData();
    } catch (err: any) {
      setControlError(err?.response?.data?.error || 'Failed to request start');
      console.error('Error requesting recorder start:', err);
    }
  };

  if (loading) return <div className="loading">Loading recorder status...</div>;
  if (error) return <div className="error">{error}</div>;

  const isAlive = statusResp?.is_alive ?? false;
  const startSupported = statusResp?.start_supported ?? false;
  // `now` ticks every second, so these re-evaluate without a timer of their own.
  const startPending = startRequestedAt !== null && now - startRequestedAt < START_TIMEOUT_MS;
  const startTimedOut = startRequestedAt !== null && now - startRequestedAt >= START_TIMEOUT_MS;
  const info = statusResp?.status;
  const storage = statusResp?.storage;
  const vconStore = statusResp?.vcon_store;
  const db = statusResp?.database;
  const stt = statusResp?.stt;
  const natOverrideActive = Boolean(
    info?.local_ip && info?.advertised_ip && info.local_ip !== info.advertised_ip
  );

  return (
    <div className="status-container">
      <div className="status-header-row">
        <h2>Recorder Status</h2>
        <button type="button" onClick={fetchData} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </button>
        {lastFetched && (
          <span className="settings-hint status-last-fetched">Updated {lastFetched.toLocaleTimeString(undefined, { hour12: false })} (auto-refreshes every 10s)</span>
        )}
      </div>

      {!isAlive && (
        <div className="error status-offline-banner">
          {info
            ? 'Recorder is offline — last heartbeat was more than 30 seconds ago.'
            : 'Recorder has never reported status. Is it running the latest build?'}
        </div>
      )}

      {info && (
        <>
          <div className="status-cards">
            <div className="status-card status-card-wide">
              <h4>Recorder uptime</h4>
              <div className="status-card-value">
                {formatDuration((now - new Date(info.started_at).getTime()) / 1000)}
              </div>
              <div className="status-card-detail">Started {formatDateTime(info.started_at)}</div>
              <div className="status-card-detail">Last heartbeat {formatDateTime(info.updated_at)}</div>
              <div className="status-card-detail" style={{ marginTop: '10px' }}>
                {/* Only meaningful while the recorder is alive to consume it —
                    a stale flag on a dead recorder would show "pending" forever. */}
                {isAlive && info.restart_requested ? (
                  <span className="status status-active">restart pending — picked up on the next heartbeat (~10s)</span>
                ) : !isAlive && startPending ? (
                  <span className="status status-active">starting — waiting for the recorder to report in…</span>
                ) : confirmRestart ? (
                  <>
                    <div>Restart the recorder? Any active recordings will be terminated.</div>
                    <div style={{ marginTop: '6px', display: 'flex', gap: '8px' }}>
                      <button type="button" className="danger" onClick={handleRestart}>Yes, restart</button>
                      <button type="button" onClick={() => setConfirmRestart(false)}>Cancel</button>
                    </div>
                  </>
                ) : isAlive ? (
                  <button type="button" onClick={() => setConfirmRestart(true)}
                    title="Gracefully restart the recorder process">
                    Restart recorder
                  </button>
                ) : (
                  /* Offline: a restart flag would sit unread, so the control
                     becomes a start instead — handled outside the recorder by
                     the launchd agent, which not every host has. No confirm
                     step: there is nothing running to interrupt. */
                  <button type="button" onClick={handleStart} disabled={!startSupported}
                    title={startSupported
                      ? 'Start the recorder on the recorder host'
                      : 'Starting from the console is not configured on this host'}>
                    Start recorder
                  </button>
                )}
                {startTimedOut && !isAlive && (
                  <div className="error" style={{ marginTop: '6px' }}>
                    The recorder has not reported in since the start request — check
                    recorder-start.log on the recorder host.
                  </div>
                )}
                {controlError && <div className="error" style={{ marginTop: '6px' }}>{controlError}</div>}
              </div>
            </div>

            <div className="status-card status-card-wide">
              <h4>SIP / RTP</h4>
              <div className="status-line">
                <span>SIP (TCP) port {info.sip_port}</span>
                <span className={`status ${info.sip_tcp_operational ? 'status-completed' : 'status-failed'}`}>
                  {info.sip_tcp_operational ? 'operational' : 'down'}
                </span>
              </div>
              <div className="status-line">
                <span>SIP (TLS){info.sip_tls_enabled ? ` port ${info.sip_tls_port}` : ''}</span>
                {info.sip_tls_enabled ? (
                  <span className={`status ${info.sip_tls_operational ? 'status-completed' : 'status-failed'}`}>
                    {info.sip_tls_operational ? 'operational' : 'down'}
                  </span>
                ) : (
                  <span className="status status-terminated">disabled</span>
                )}
              </div>
              <div className="status-line">
                <span>RTP {info.rtp_port_base}–{info.rtp_port_max}</span>
                <span className="status status-active">
                  {info.rtp_ports_in_use.length} in use · {Math.max(0, (info.rtp_port_max - info.rtp_port_base + 1) - info.rtp_ports_in_use.length)} available
                </span>
              </div>
              {info.rtp_ports_in_use.length > 0 && (
                <div className="status-card-detail monospace small">{info.rtp_ports_in_use.join(', ')}</div>
              )}
              <div className="status-line">
                <span>WAN</span>
                <span className="monospace">{info.advertised_ip || '—'}</span>
              </div>
              <div className="status-line">
                <span>LAN</span>
                <span className="monospace">{info.local_ip || '—'}</span>
              </div>
            </div>

            <div className="status-card">
              <h4>Security</h4>
              <div className="status-line">
                <span>SRTP</span>
                <span className={`status ${info.srtp_enabled ? 'status-completed' : 'status-terminated'}`}>
                  {info.srtp_enabled ? 'enabled' : 'disabled'}
                </span>
              </div>
              <div className="status-line">
                <span>SIP/RTP IP filtering</span>
                <span className={`status ${info.ip_filtering_enabled ? 'status-completed' : 'status-terminated'}`}>
                  {info.ip_filtering_enabled ? 'enabled' : 'disabled'}
                </span>
              </div>
              <div className="status-line">
                <span>NAT override</span>
                <span className={`status ${natOverrideActive ? 'status-completed' : 'status-terminated'}`}>
                  {natOverrideActive ? 'active' : 'inactive'}
                </span>
              </div>
            </div>

            {storage && (
              <div className="status-card status-card-full">
                <h4>System resources</h4>
                <div className="status-card-columns">
                <div>
                {storage.same_volume === false ? (
                  /* Split stores: one used/available pair would sum bytes
                     across two filesystems and compare them against the free
                     space of just one, so each volume gets its own bar. */
                  <>
                    <div className="status-card-detail">Recording storage — WAV volume</div>
                    <div className="storage-bar">
                      <div
                        className="storage-bar-used"
                        style={{
                          width: `${Math.min(100, ((storage.recordings_bytes ?? 0) / (((storage.recordings_bytes ?? 0) + (storage.recordings_available_bytes ?? 0)) || 1)) * 100)}%`,
                        }}
                      />
                    </div>
                    <div className="status-card-detail">
                      {formatBytes(storage.recordings_bytes ?? 0)} WAV
                      {' · '}{formatBytes(storage.recordings_available_bytes ?? 0)} available
                    </div>

                    <div className="status-card-detail" style={{ marginTop: '10px' }}>Recording storage — vCon volume</div>
                    <div className="storage-bar">
                      <div
                        className="storage-bar-used"
                        style={{
                          width: `${Math.min(100, ((storage.vcons_bytes ?? 0) / (((storage.vcons_bytes ?? 0) + (storage.vcons_available_bytes ?? 0)) || 1)) * 100)}%`,
                        }}
                      />
                    </div>
                    <div className="status-card-detail">
                      {formatBytes(storage.vcons_bytes ?? 0)} vCon
                      {' · '}{formatBytes(storage.vcons_available_bytes ?? 0)} available
                    </div>
                  </>
                ) : (
                  <>
                    <div className="status-card-detail">Recording storage</div>
                    <div className="storage-bar">
                      <div
                        className="storage-bar-used"
                        style={{
                          width: `${Math.min(100, (storage.used_bytes / (storage.used_bytes + storage.available_bytes || 1)) * 100)}%`,
                        }}
                      />
                    </div>
                    <div className="status-card-detail">
                      {formatBytes(storage.used_bytes)} used · {formatBytes(storage.available_bytes)} available
                    </div>
                    {(storage.recordings_bytes !== undefined || storage.vcons_bytes !== undefined) && (
                      <div className="status-card-detail storage-breakdown">
                        {formatBytes(storage.recordings_bytes ?? 0)} WAV
                        {' · '}
                        {formatBytes(storage.vcons_bytes ?? 0)} vCon
                      </div>
                    )}
                  </>
                )}

                {/* Counted from the index, not the filesystem: it is the only
                    thing that knows about vCons moved to object storage.
                    Outside the volume ternary above so it shows whether or not
                    the stores are split. */}
                {vconStore && (vconStore.gcs_count > 0 || vconStore.local_count > 0) && (
                  <div className="status-card-detail vcon-store-split">
                    <span className="vcon-store-label">vCon store</span>
                    {' '}
                    {vconStore.local_count.toLocaleString()} local
                    {' ('}{formatBytes(vconStore.local_bytes)}{')'}
                    {' · '}
                    {vconStore.gcs_count.toLocaleString()} in Cloud Storage
                    {' ('}{formatBytes(vconStore.gcs_bytes)}{')'}
                    {vconStore.gcs_bucket && (
                      <span className="vcon-store-bucket"> — {vconStore.gcs_bucket}</span>
                    )}
                  </div>
                )}

                <div className="status-card-detail" style={{ marginTop: '10px' }}>CPU</div>
                {info.cpu_percent !== null && info.cpu_percent !== undefined ? (
                  <>
                    <div className="storage-bar">
                      <div className="storage-bar-used" style={{ width: `${Math.min(100, Number(info.cpu_percent))}%` }} />
                    </div>
                    <div className="status-card-detail">
                      {Number(info.cpu_percent).toFixed(1)}% used · {(100 - Number(info.cpu_percent)).toFixed(1)}% available
                    </div>
                  </>
                ) : (
                  <div className="status-card-detail">measuring…</div>
                )}

                <div className="status-card-detail" style={{ marginTop: '10px' }}>Memory</div>
                {info.mem_total_bytes ? (
                  <>
                    <div className="storage-bar">
                      <div
                        className="storage-bar-used"
                        style={{
                          width: `${Math.min(100, ((Number(info.mem_total_bytes) - Number(info.mem_available_bytes || 0)) / Number(info.mem_total_bytes)) * 100)}%`,
                        }}
                      />
                    </div>
                    <div className="status-card-detail">
                      {formatBytes(Number(info.mem_total_bytes) - Number(info.mem_available_bytes || 0))} used
                      {' · '}{formatBytes(Number(info.mem_available_bytes || 0))} available
                      {' ('}{(((Number(info.mem_total_bytes) - Number(info.mem_available_bytes || 0)) / Number(info.mem_total_bytes)) * 100).toFixed(1)}%{')'}
                    </div>
                  </>
                ) : (
                  <div className="status-card-detail">unavailable</div>
                )}
                </div>

                <div>
                <div className="status-line">
                  <span>Load average (1/5/15m)</span>
                  <span className="monospace">
                    {info.load_avg_1m !== null && info.load_avg_1m !== undefined
                      ? `${Number(info.load_avg_1m).toFixed(2)} / ${Number(info.load_avg_5m ?? 0).toFixed(2)} / ${Number(info.load_avg_15m ?? 0).toFixed(2)}`
                      : '—'}
                  </span>
                </div>
                <div className="status-line">
                  <span>Recorder process</span>
                  <span className="monospace">
                    {info.proc_cpu_percent !== null && info.proc_cpu_percent !== undefined
                      ? `CPU ${Number(info.proc_cpu_percent).toFixed(1)}%`
                      : 'CPU —'}
                    {' · RSS '}
                    {info.proc_rss_bytes ? formatBytes(Number(info.proc_rss_bytes)) : '—'}
                  </span>
                </div>
                <div className="status-line">
                  <span>Swap</span>
                  <span className="monospace">
                    {info.swap_total_bytes !== null && info.swap_total_bytes !== undefined
                      ? Number(info.swap_total_bytes) > 0
                        ? `${formatBytes(Number(info.swap_used_bytes || 0))} used of ${formatBytes(Number(info.swap_total_bytes))}`
                        : 'none configured'
                      : '—'}
                  </span>
                </div>
                <div className="status-line">
                  <span>Network RX</span>
                  <span className="monospace">
                    {info.net_rx_bytes_per_sec !== null && info.net_rx_bytes_per_sec !== undefined
                      ? `${formatBytes(Number(info.net_rx_bytes_per_sec))}/s`
                      : 'measuring…'}
                  </span>
                </div>
                <div className="status-line">
                  <span>Database</span>
                  <span>
                    {db ? (
                      <>
                        <span className={`status ${db.connected ? 'status-completed' : 'status-failed'}`}>
                          {db.connected ? 'connected' : 'unreachable'}
                        </span>
                        {db.connected && (
                          <span className="monospace small">
                            {' '}{db.size_bytes !== null ? formatBytes(Number(db.size_bytes)) : '—'}
                            {db.ping_ms !== null ? ` · ping ${Number(db.ping_ms).toFixed(1)} ms` : ''}
                          </span>
                        )}
                      </>
                    ) : '—'}
                  </span>
                </div>
                </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {stt && (
        <div className="status-cards">
          <div className="status-card status-card-full">
            <h4>Speech recognition</h4>
            <div className="status-line">
              <span>Engine</span>
              <span className="monospace">
                {stt.engine}{stt.model ? ` (${stt.model})` : ''} · {stt.local ? 'local — audio stays on this host' : 'cloud'}
              </span>
            </div>
            {stt.local && (
              <>
                <div className="status-line">
                  <span>Whisper server</span>
                  <span className={`status ${stt.reachable ? 'status-completed' : 'status-failed'}`}>
                    {stt.reachable
                      ? `running${stt.response_ms != null ? ` · answered in ${stt.response_ms} ms` : ''}`
                      : stt.process
                        ? 'process up but not answering'
                        : 'not running'}
                  </span>
                </div>
                <div className="status-line">
                  <span>Whisper process</span>
                  <span className="monospace">
                    {stt.process
                      ? `pid ${stt.process.pid} · up ${formatDuration(stt.process.uptime_seconds)} · CPU ${stt.process.cpu_percent.toFixed(1)}% · RSS ${formatBytes(stt.process.rss_bytes)}`
                      : '—'}
                  </span>
                </div>
              </>
            )}
            <div className="status-line">
              <span>Last 24 hours</span>
              <span>
                {stt.transcriptions_24h} transcribed
                {stt.failures_24h > 0 && (
                  <span className="status status-failed" style={{ marginLeft: '6px' }}>{stt.failures_24h} failed</span>
                )}
                {stt.last_transcription && (
                  <span className="small"> · last {formatDateTime(stt.last_transcription)}</span>
                )}
              </span>
            </div>
            {stt.local && !stt.reachable && (
              <div className="status-card-detail" style={{ marginTop: '6px' }}>
                Transcriptions will fail until it is back. It runs as the LaunchAgent
                com.12by9.whisper-server, which launchd restarts on its own; if it stays
                down, check /opt/homebrew/var/log/whisper-server.log.
              </div>
            )}
          </div>
        </div>
      )}

      <h3>Active recordings</h3>
      <div className="table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th>Group ID</th>
              <th>Group Seq</th>
              <th>Associate Time</th>
              <th>callingParty</th>
              <th>calledParty</th>
              <th>byAction</th>
              <th>Invite Source</th>
              <th>Media Sources</th>
              <th>Local RTP Ports</th>
              <th>Duration</th>
              <th>Bytes</th>
            </tr>
          </thead>
          <tbody>
            {activeRecordings.length === 0 ? (
              <tr><td colSpan={11} className="no-data">No active recordings</td></tr>
            ) : (
              activeRecordings.map(session => {
                const durationSeconds = (now - new Date(session.start_time).getTime()) / 1000;
                const localPorts = session.participants
                  .map(p => p.rtp_port)
                  .filter((port): port is number => port !== null);
                return (
                  <tr key={session.id}>
                    <td className="monospace small">{session.group_id || '—'}</td>
                    <td>{session.group_seq ?? '—'}</td>
                    <td>{formatDateTime(session.associate_time)}</td>
                    <td className="small">{session.calling_party_name || '—'} <span className="monospace small">&lt;{session.calling_party_aor || '—'}&gt;</span></td>
                    <td className="small">{session.called_party_name || '—'} <span className="monospace small">&lt;{session.called_party_aor || '—'}&gt;</span></td>
                    <td>
                      {session.by_action || '—'}
                      {session.xferred_group_id && (
                        <div className="monospace small" title="xferredGroupID">↩ {session.xferred_group_id}</div>
                      )}
                    </td>
                    <td className="monospace small">{session.invite_source_ip ? `${session.invite_source_ip}:${session.invite_source_port ?? '—'}` : '—'}</td>
                    <td className="monospace small">
                      {session.participants.some(p => p.participant_ip) ? (
                        session.participants
                          .filter(p => p.participant_ip)
                          .map((p, i) => (
                            <div key={i} title={`${p.participant_role} → local port ${p.rtp_port ?? '—'}`}>
                              {p.participant_ip}:{p.participant_source_port ?? '—'}
                            </div>
                          ))
                      ) : '—'}
                    </td>
                    <td className="monospace small">{localPorts.length > 0 ? localPorts.join(', ') : '—'}</td>
                    <td>{durationSeconds >= 0 ? formatDuration(durationSeconds) : '—'}</td>
                    <td>{formatBytes(session.total_bytes_recorded)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StatusPage;
